
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { Card, Button, Table, Thead, Tbody, Tr, Th, Td } from '../components/UI';
import { ShoppingCart, Trash2, Plus, Minus, Package, ArrowRight, ChevronRight } from '../components/Icons';

const SupplierCart: React.FC = () => {
  const { items, removeItem, updateQuantity, clearCart, totalItems, totalPrice } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handlePlaceOrder = () => {
    alert('Order placed successfully! Transaction ID: TX-' + Math.floor(Math.random() * 1000000));
    clearCart();
    navigate('/supplier-orders');
  };

  if (items.length === 0) {
    return (
      <div className="p-6 space-y-6 max-w-[1400px] mx-auto animate-in fade-in duration-500">
        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
          <span>{user?.companyName || 'Supplier'}</span>
          <ChevronRight size={10} />
          <span className="text-brand-dark dark:text-white">{t('cart_title')}</span>
        </div>
        <h1 className="text-3xl font-light text-[#161616] dark:text-white mb-8">{t('cart_title')}</h1>
        
        <Card className="flex flex-col items-center justify-center py-32 text-center space-y-6 border-dashed border-2 bg-transparent rounded-sm">
           <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A1A1A] rounded-full flex items-center justify-center text-gray-300">
              <ShoppingCart size={32} />
           </div>
           <div className="space-y-1">
              <h3 className="text-lg font-bold text-brand-dark dark:text-white uppercase tracking-widest">{t('empty_cart')}</h3>
              <p className="text-xs text-gray-400 max-w-xs mx-auto font-medium">
                 {t('empty_cart_desc')}
              </p>
           </div>
           <Button onClick={() => navigate('/supplier-products')} className="px-10 h-11 text-[10px] font-bold tracking-widest">
              {t('go_to_products')} <ArrowRight size={14} className="ml-2" />
           </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 space-y-4 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      {/* Header with Breadcrumbs and Clear Action */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
            <span>{user?.companyName || 'Supplier'}</span>
            <ChevronRight size={10} />
            <span className="text-brand-dark dark:text-white">{t('cart_title')}</span>
          </div>
          <h1 className="text-4xl font-light text-[#161616] dark:text-white tracking-tight">{t('cart_title')}</h1>
        </div>
        <button 
          onClick={clearCart}
          className="flex items-center gap-2 text-red-500 hover:text-red-700 text-[10px] font-bold uppercase tracking-widest transition-colors mt-6"
        >
          <Trash2 size={16} /> {t('clear_all')}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 items-start mt-8">
        {/* Cart Items List */}
        <div className="xl:col-span-8">
          <div className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] rounded-sm overflow-hidden border-t-4 border-t-brand-medium shadow-sm">
            <Table>
              <Thead>
                <Tr isHeader className="bg-gray-50/30">
                  <Th className="py-4">{t('item_name')}</Th>
                  <Th className="text-right py-4">{t('unit_price')}</Th>
                  <Th className="text-center py-4">{t('quantity')}</Th>
                  <Th className="text-right py-4">{t('total')}</Th>
                  <Th className="w-16"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((item) => (
                  <Tr key={item.id} className="group">
                    <Td className="py-6">
                      <div className="flex flex-col">
                        <span className="font-bold text-brand-dark dark:text-white text-base">{item.itemName}</span>
                        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest mt-1">{item.itemNumber}</span>
                      </div>
                    </Td>
                    <Td className="text-right py-6 font-medium text-gray-600 dark:text-gray-400">
                      CHF {item.price.toFixed(2)}
                    </Td>
                    <Td className="py-6">
                      <div className="flex items-center justify-center gap-4">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-sm bg-gray-100 dark:bg-[#262626] flex items-center justify-center hover:bg-gray-200 text-gray-500 transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm font-black w-6 text-center text-brand-dark dark:text-white">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-sm bg-gray-100 dark:bg-[#262626] flex items-center justify-center hover:bg-gray-200 text-gray-500 transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </Td>
                    <Td className="text-right py-6">
                      <span className="font-bold text-brand-dark dark:text-white">
                        CHF {(item.price * item.quantity).toFixed(2)}
                      </span>
                    </Td>
                    <Td className="text-right py-6">
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="p-2 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        title="Remove"
                      >
                        <Trash2 size={18} />
                      </button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        </div>

        {/* Order Summary Sidebar */}
        <div className="xl:col-span-4 space-y-6">
          <Card className="p-10 space-y-8 bg-brand-dark text-white border-none shadow-2xl relative overflow-hidden rounded-sm">
            {/* Subtle Texture/Overlay */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mt-24 blur-3xl"></div>
            
            <h3 className="text-lg font-bold uppercase tracking-[0.25em] relative z-10 text-white/90">{t('order_summary')}</h3>
            
            <div className="space-y-5 relative z-10">
              <div className="flex justify-between text-white/50 text-[11px] font-black uppercase tracking-widest">
                <span>{t('subtotal').replace('{count}', String(totalItems))}</span>
                <span className="text-white">CHF {totalPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white/50 text-[11px] font-black uppercase tracking-widest">
                <span>{t('estimated_tax')} (8.1%)</span>
                <span className="text-white">CHF {(totalPrice * 0.081).toFixed(2)}</span>
              </div>
              
              <div className="pt-8 border-t border-white/10 flex justify-between items-center">
                <span className="text-xs font-black uppercase tracking-[0.2em] text-white/80">{t('grand_total')}</span>
                <span className="text-4xl font-black text-brand-bright tracking-tighter">
                  CHF {(totalPrice * 1.081).toLocaleString('de-CH', { minimumFractionDigits: 3 })}
                </span>
              </div>
            </div>

            <div className="space-y-6 pt-4 relative z-10">
              <Button 
                onClick={handlePlaceOrder}
                className="w-full h-16 !bg-brand-bright !text-brand-dark font-black text-xs tracking-[0.3em] shadow-xl hover:brightness-110 active:scale-[0.98] transition-all rounded-sm"
              >
                {t('place_order')}
              </Button>
              <p className="text-[10px] text-white/30 text-center uppercase font-bold tracking-[0.15em] leading-relaxed px-4">
                {t('cart_legal_disclaimer')}
              </p>
            </div>
          </Card>

          {/* Fulfillment Badge Panel */}
          <Card className="p-6 bg-white dark:bg-[#111111] border-gray-100 dark:border-[#262626] flex items-start gap-5 shadow-sm rounded-sm">
             <div className="w-10 h-10 bg-gray-50 dark:bg-[#1A1A1A] rounded-full flex items-center justify-center shrink-0 border border-gray-100 dark:border-[#333]">
                <Package size={20} className="text-brand-medium" />
             </div>
             <div className="space-y-1">
                <h4 className="text-[10px] font-black text-brand-dark dark:text-white uppercase tracking-widest">{t('fulfillment_badge_title')}</h4>
                <p className="text-[11px] text-gray-500 font-medium leading-relaxed">
                   {t('fulfillment_badge_desc')}
                </p>
             </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupplierCart;
